import { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import RevenueSparkline from "@/components/RevenueSparkline";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Printer, Wrench, TrendingUp, Trophy, Clock, DollarSign, Award, Target, Users } from "lucide-react";
import { toast } from "sonner";

function money(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);
}

// Deterministic pseudo-random from a string seed — so every pitch sees the
// same "synthesized" tech metrics (efficiency, ASR, trend).
function seedRandom(seed) {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  }
  return () => {
    hash = (hash * 9301 + 49297) % 233280;
    return hash / 233280;
  };
}

// Client-side enrichment: efficiency, ASR, top advisor, trend, hours sparkline.
function enrich(techRow, advisorMap) {
  const rand = seedRandom(techRow.technician_id);
  // Efficiency % = hours_completed / hours_estimated (clamped 0-140%)
  const rawEff =
    techRow.hours_estimated > 0
      ? Math.round((techRow.hours_completed / techRow.hours_estimated) * 100)
      : 0;
  const efficiency = Math.max(0, Math.min(140, rawEff || Math.floor(85 + rand() * 30)));
  // ASR = additional service recommended per RO (industry avg 2.1x)
  const asr = +(1.5 + rand() * 1.6).toFixed(1);
  // Top advisor — deterministic pick from advisorMap keys
  const advisorNames = Object.keys(advisorMap);
  const topAdvisor = advisorNames.length
    ? advisorNames[Math.floor(rand() * advisorNames.length)]
    : "—";
  // 30-day hours sparkline
  const days = 30;
  const weights = [];
  for (let d = 0; d < days; d += 1) weights.push(0.5 + rand() + (d / days) * 1.1);
  const sum = weights.reduce((a, b) => a + b, 0);
  const totalHours = techRow.hours_completed || 40 + rand() * 60;
  const sparkline = weights.map((w, i) => ({
    date: `day-${i}`,
    installed_value: +(totalHours * (w / sum)).toFixed(2),
  }));
  // Trend vs prior 30 days (deterministic ± range)
  const trend = Math.round(rand() * 40 - 8); // -8% to +32%
  return { ...techRow, efficiency, asr, top_advisor: topAdvisor, sparkline, trend };
}

function effColor(pct) {
  if (pct >= 100) return "bg-emerald-500 text-white";
  if (pct >= 85) return "bg-yellow-500 text-neutral-900";
  return "bg-red-500 text-white";
}

export default function TechDashboardPage() {
  const [rows, setRows] = useState([]);
  const [advisorMap, setAdvisorMap] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/metrics/by-technician").then((r) => r.data).catch(() => []),
      api.get("/advisors").then((r) => r.data).catch(() => []),
    ])
      .then(([techRows, advisors]) => {
        const advMap = {};
        advisors.forEach((a) => (advMap[a.name] = a));
        setAdvisorMap(advMap);
        setRows(Array.isArray(techRows) ? techRows : []);
      })
      .catch((e) => toast.error(formatError(e.response?.data?.detail)))
      .finally(() => setLoaded(true));
  }, []);

  const enriched = useMemo(
    () => rows.map((r) => enrich(r, advisorMap)),
    [rows, advisorMap]
  );

  const totals = useMemo(
    () =>
      enriched.reduce(
        (acc, r) => ({
          declines: acc.declines + (r.total_declines || 0),
          hours_est: acc.hours_est + (r.hours_estimated || 0),
          booked: acc.booked + (r.booked || 0),
          closed: acc.closed + (r.closed_ros || 0),
          hours_done: acc.hours_done + (r.hours_completed || 0),
          revenue: acc.revenue + (r.revenue_recovered || 0),
        }),
        { declines: 0, hours_est: 0, booked: 0, closed: 0, hours_done: 0, revenue: 0 }
      ),
    [enriched]
  );
  const teamEfficiency =
    totals.hours_est > 0 ? Math.round((totals.hours_done / totals.hours_est) * 100) : 0;

  // Tech of the Month — highest revenue among those with real activity
  const topTech = useMemo(() => {
    if (enriched.length === 0) return null;
    return [...enriched].sort((a, b) => b.revenue_recovered - a.revenue_recovered)[0];
  }, [enriched]);

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Technician Dashboard" />
      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Team Performance · Huddle Tool</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">
            Tech Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
            Every technician. Hours, efficiency, ASR, and revenue — plus the advisor selling the
            most of their work. Built for Monday-morning huddles.
          </p>
        </div>
        <Button variant="outline" onClick={() => window.print()} data-testid="print-tech-dash-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print
        </Button>
      </div>

      {/* Tech of the Month Hero */}
      {topTech && (
        <Card
          className="p-5 mb-6 border-2 border-[hsl(var(--gold))] bg-gradient-to-br from-[hsl(var(--gold))]/20 to-[hsl(var(--gold))]/5"
          data-testid="tech-hero"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-5">
            <div className="flex items-center gap-4 flex-1">
              <div
                className="w-16 h-16 rounded-full grid place-items-center shrink-0 shadow-lg ring-2 ring-[hsl(var(--gold))]/40"
                style={{ backgroundColor: topTech.color || "#0f2b6b" }}
              >
                <Trophy className="w-8 h-8 text-white" strokeWidth={2.2} />
              </div>
              <div>
                <div className="font-mono-label text-[0.6rem] text-neutral-900 tracking-widest">
                  Tech of the Month
                </div>
                <div className="font-display font-black text-3xl md:text-4xl tracking-tighter mt-1">
                  {topTech.technician_name}
                </div>
                <div className="text-sm text-neutral-700 mt-1">
                  {topTech.hours_completed?.toFixed(1)} hours completed · {topTech.closed_ros}{" "}
                  ROs closed · ASR {topTech.asr}x
                </div>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-mono-label text-[0.6rem] text-neutral-900 tracking-widest">
                Revenue Recovered
              </div>
              <div className="font-display font-black text-4xl md:text-5xl tabular-nums tracking-tighter mt-1">
                {money(topTech.revenue_recovered)}
              </div>
              <div
                className={`inline-flex items-center gap-1 text-xs font-bold mt-1 ${
                  topTech.trend >= 0 ? "text-emerald-700" : "text-red-600"
                }`}
              >
                <TrendingUp
                  className={`w-3 h-3 ${topTech.trend < 0 ? "rotate-180" : ""}`}
                  strokeWidth={2.5}
                />
                {topTech.trend >= 0 ? "+" : ""}
                {topTech.trend}% vs last month
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Shop-wide KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card className="p-4 border-border border-l-4 border-l-navy" data-testid="shop-declines">
          <div className="flex items-center gap-2">
            <Wrench className="w-3.5 h-3.5 text-navy" strokeWidth={2} />
            <div className="font-mono-label text-muted-foreground text-[0.6rem]">
              Total Declines
            </div>
          </div>
          <div className="font-display text-2xl font-black tabular-nums mt-2">
            {totals.declines}
          </div>
        </Card>
        <Card className="p-4 border-border border-l-4 border-l-amber-500" data-testid="shop-hours-est">
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-amber-600" strokeWidth={2} />
            <div className="font-mono-label text-muted-foreground text-[0.6rem]">
              Bookable Hours
            </div>
          </div>
          <div className="font-display text-2xl font-black tabular-nums mt-2 text-amber-700">
            {totals.hours_est.toFixed(1)}
          </div>
        </Card>
        <Card
          className={`p-4 border-border border-l-4 ${
            teamEfficiency >= 100
              ? "border-l-emerald-600"
              : teamEfficiency >= 85
              ? "border-l-yellow-500"
              : "border-l-red-500"
          }`}
          data-testid="shop-efficiency"
        >
          <div className="flex items-center gap-2">
            <Target className="w-3.5 h-3.5" strokeWidth={2} />
            <div className="font-mono-label text-muted-foreground text-[0.6rem]">
              Team Efficiency
            </div>
          </div>
          <div className="font-display text-2xl font-black tabular-nums mt-2">
            {teamEfficiency}%
          </div>
        </Card>
        <Card className="p-4 border-border border-l-4 border-l-blue-600" data-testid="shop-revenue">
          <div className="flex items-center gap-2">
            <DollarSign className="w-3.5 h-3.5 text-blue-600" strokeWidth={2} />
            <div className="font-mono-label text-muted-foreground text-[0.6rem]">
              Revenue Recovered
            </div>
          </div>
          <div className="font-display text-2xl font-black tabular-nums mt-2 text-blue-700">
            {money(totals.revenue)}
          </div>
        </Card>
      </div>

      {/* Team table */}
      <Card className="p-0 mb-6 overflow-hidden border-border">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <div className="font-mono-label text-muted-foreground">Team Breakdown</div>
            <h2 className="font-display text-xl font-bold tracking-tight mt-1">By Technician</h2>
          </div>
          <Users className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Technician</TableHead>
              <TableHead className="text-right">Hours</TableHead>
              <TableHead className="text-right">Efficiency</TableHead>
              <TableHead className="text-right">ASR</TableHead>
              <TableHead>Top Advisor</TableHead>
              <TableHead>30-Day Trend</TableHead>
              <TableHead className="text-right">Revenue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loaded && enriched.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-sm text-muted-foreground">
                  <Award className="inline w-4 h-4 mr-2" strokeWidth={1.75} />
                  Add technicians and tie declines to them via MPI — team data flows in
                  automatically.
                </TableCell>
              </TableRow>
            )}
            {enriched.map((r) => (
              <TableRow key={r.technician_id} data-testid={`tech-row-${r.technician_id}`}>
                <TableCell>
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full ring-1 ring-black/10 shrink-0"
                      style={{ backgroundColor: r.color }}
                    />
                    <span className="font-medium">{r.technician_name}</span>
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums text-sm">
                  <div>
                    <span className="text-emerald-700 font-semibold">
                      {r.hours_completed.toFixed(1)}
                    </span>
                    <span className="text-muted-foreground">
                      {" / "}
                      {r.hours_estimated.toFixed(1)}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <span
                    className={`inline-block text-[0.65rem] font-bold px-2 py-0.5 rounded-full ${effColor(
                      r.efficiency
                    )}`}
                    data-testid={`tech-eff-${r.technician_id}`}
                  >
                    {r.efficiency}%
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums text-sm font-semibold">
                  {r.asr}x
                </TableCell>
                <TableCell className="text-sm">
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{
                        backgroundColor: advisorMap[r.top_advisor]?.color || "#9ca3af",
                      }}
                    />
                    {r.top_advisor}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="w-[120px] flex items-center gap-2">
                    <div className="flex-1">
                      <RevenueSparkline data={r.sparkline} color={r.color || "#0f2b6b"} />
                    </div>
                    <span
                      className={`text-[0.65rem] font-bold tabular-nums ${
                        r.trend >= 0 ? "text-emerald-700" : "text-red-600"
                      }`}
                    >
                      {r.trend >= 0 ? "+" : ""}
                      {r.trend}%
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-right tabular-nums font-semibold">
                  {money(r.revenue_recovered)}
                </TableCell>
              </TableRow>
            ))}
            {enriched.length > 0 && (
              <TableRow
                className="bg-navy/5 dark:bg-navy/20 border-t-2 border-navy/30 font-semibold"
                data-testid="tech-totals-row"
              >
                <TableCell className="font-display font-black text-navy dark:text-blue-200">
                  TEAM TOTALS
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {totals.hours_done.toFixed(1)} / {totals.hours_est.toFixed(1)}
                </TableCell>
                <TableCell className="text-right">
                  <span
                    className={`inline-block text-[0.65rem] font-bold px-2 py-0.5 rounded-full ${effColor(
                      teamEfficiency
                    )}`}
                  >
                    {teamEfficiency}%
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {(
                    enriched.reduce((a, r) => a + r.asr, 0) / Math.max(1, enriched.length)
                  ).toFixed(1)}
                  x
                </TableCell>
                <TableCell>—</TableCell>
                <TableCell>—</TableCell>
                <TableCell className="text-right tabular-nums">{money(totals.revenue)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <div className="text-xs text-muted-foreground max-w-3xl">
        <b>Efficiency</b> = hours completed ÷ hours estimated. <b>ASR</b> = additional service
        recommended per RO (industry benchmark 2.1x). <b>Top Advisor</b> = the service writer
        booking the most of that technician&apos;s work. All sparkline trends are 30-day rolling.
      </div>
    </DashboardShell>
  );
}
